import { Box, Stack, Typography } from '@mui/material';

import { type DrawerCutsheetInfo } from './types';
import {
  ErrorBar,
  LockedBar,
  LockedCutsheetActions,
  NoCutsheetActions,
  SuccessBar,
  UnlockedCutsheetActions,
} from './split-slot-parts';

interface BuyerSplitSlotProps {
  label?: string;
  existingBuyer?: DrawerCutsheetInfo;
  cutsheetName?: string;
  isLocked?: boolean;
  processorName?: string;
  downloadingPdf?: boolean;
  readOnly?: boolean;
  onFillCutsheet: () => void;
  onEditCutsheet: () => void;
  onDownloadPdf: () => void;
  onPreviewCutsheet?: () => void;
  isQuestionnaireResponse?: boolean;
}

export function BuyerSplitSlot({
  label = undefined,
  existingBuyer = undefined,
  cutsheetName = undefined,
  isLocked = false,
  processorName = undefined,
  downloadingPdf = false,
  readOnly = false,
  onFillCutsheet,
  onEditCutsheet,
  onDownloadPdf,
  onPreviewCutsheet = undefined,
  isQuestionnaireResponse = false,
}: BuyerSplitSlotProps) {
  const buyerUser = existingBuyer?.contactUser;

  if (!buyerUser) return null;

  const showError = !cutsheetName && !isLocked;

  let cutsheetActions: React.ReactNode = null;

  if (isLocked && cutsheetName) {
    cutsheetActions = (
      <LockedCutsheetActions
        downloadingPdf={downloadingPdf}
        onDownloadPdf={onDownloadPdf}
      />
    );
  } else if (cutsheetName) {
    cutsheetActions = (
      <UnlockedCutsheetActions
        downloadingPdf={downloadingPdf}
        onFillCutsheet={onFillCutsheet}
        onEditCutsheet={onEditCutsheet}
        onDownloadPdf={onDownloadPdf}
      />
    );
  } else if (!isLocked) {
    cutsheetActions = <NoCutsheetActions onFillCutsheet={onFillCutsheet} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      {/* Header: label only (no buyer name or change menu in buyer mode) */}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {label ?? 'Whole'}
          </Typography>
        </Box>
      </Stack>

      {/* Locked bar */}
      {isLocked && (
        <LockedBar processorName={processorName} cutsheetName={cutsheetName} />
      )}

      {/* Error bar: cutsheet not filled in */}
      {showError && <ErrorBar message="Cutsheet not filled in" />}

      {/* Success bar: cutsheet assigned (not shown when locked) */}
      {cutsheetName && !isLocked && (
        <SuccessBar
          cutsheetName={cutsheetName}
          isResponse={isQuestionnaireResponse}
          onPreview={onPreviewCutsheet}
        />
      )}

      {/* Action buttons */}
      {!readOnly && cutsheetActions && (
        <Stack
          direction="row"
          gap={1}
          alignItems="center"
          sx={{ mt: 'auto', pt: 1.5 }}
        >
          {cutsheetActions}
        </Stack>
      )}
    </Box>
  );
}
